<template>
  <main class="aipath-home-refined">
    <section class="home-hero">
      <div class="hero-grid" aria-hidden="true"></div>
      <div class="container hero-layout">
        <div class="hero-copy">
          <p class="eyebrow">{{ home.hero.eyebrow }}</p>
          <h1>{{ home.hero.title }}</h1>
          <p class="hero-subtitle">{{ home.hero.subtitle }}</p>
          <p>{{ home.hero.text }}</p>
          <div class="hero-actions">
            <router-link to="/project-idea" class="btn-main">{{ home.hero.primary }}</router-link>
            <a href="#case-studies" class="text-link text-link--light">{{ home.hero.secondary }}</a>
          </div>
        </div>

        <figure class="hero-visual digital-core-visual" aria-hidden="true">
          <svg class="digital-core-svg" viewBox="0 0 620 460" focusable="false">
            <defs>
              <radialGradient id="digitalCoreGlow" cx="62%" cy="48%" r="38%">
                <stop offset="0%" stop-color="rgba(130,242,181,0.13)" />
                <stop offset="54%" stop-color="rgba(78,207,154,0.045)" />
                <stop offset="100%" stop-color="rgba(130,242,181,0)" />
              </radialGradient>
              <linearGradient id="cubeEdge" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stop-color="rgba(166,255,208,0.98)" />
                <stop offset="100%" stop-color="rgba(130,242,181,0.76)" />
              </linearGradient>
            </defs>

            <rect class="core-fade" width="620" height="460" />

            <g class="cube-stage">
              <g class="digital-cube">
                <g class="cube-unit cube-unit--outer">
                  <polygon class="cube-surface cube-surface--top" points="278,150 382,106 484,156 376,206" />
                  <polygon class="cube-surface cube-surface--front" points="278,150 376,206 376,326 278,264" />
                  <polygon class="cube-surface cube-surface--side" points="376,206 484,156 484,272 376,326" />
                  <polyline class="cube-edge cube-edge--primary" points="278,150 382,106 484,156 484,272 376,326 278,264 278,150 376,206 484,156" />
                  <path class="cube-edge" d="M376 206 376 326M382 106 382 220M278 264 382 220 484 272" />
                </g>
                <g class="cube-unit cube-unit--middle">
                  <polygon class="cube-surface cube-surface--top" points="314,184 382,154 450,188 378,222" />
                  <polygon class="cube-surface cube-surface--front" points="314,184 378,222 378,294 314,254" />
                  <polygon class="cube-surface cube-surface--side" points="378,222 450,188 450,258 378,294" />
                  <polyline class="cube-edge" points="314,184 382,154 450,188 450,258 378,294 314,254 314,184 378,222 450,188" />
                  <path class="cube-edge cube-edge--soft" d="M378 222 378 294M382 154 382 226M314 254 382 226 450 258" />
                </g>
                <g class="cube-unit cube-unit--inner">
                  <polygon class="cube-surface cube-surface--top" points="342,210 382,192 422,212 380,232" />
                  <polygon class="cube-surface cube-surface--front" points="342,210 380,232 380,272 342,248" />
                  <polygon class="cube-surface cube-surface--side" points="380,232 422,212 422,250 380,272" />
                  <polyline class="cube-edge cube-edge--primary" points="342,210 382,192 422,212 422,250 380,272 342,248 342,210 380,232 422,212" />
                </g>
              </g>
            </g>
          </svg>
        </figure>
      </div>
    </section>

    <section id="services" class="section section--light">
      <div class="container">
        <div class="section-heading">
          <p class="eyebrow">{{ home.servicesHeader.eyebrow }}</p>
          <h2>{{ home.servicesHeader.title }}</h2>
          <p>{{ home.servicesHeader.text }}</p>
        </div>

        <div class="service-grid horizontal-mobile" aria-label="AIPath services">
          <article v-for="service in localizedServices" :key="service.title" class="service-card">
            <div class="service-icon">{{ service.index }}</div>
            <h3>{{ service.title }}</h3>
            <p>{{ service.text }}</p>
            <ul>
              <li v-for="item in service.features" :key="item">{{ item }}</li>
            </ul>
            <router-link :to="service.path">{{ $t("common.readService") }}</router-link>
          </article>
        </div>
      </div>
    </section>

    <section id="case-studies" class="section section--white">
      <div class="container">
        <div class="section-heading">
          <p class="eyebrow">{{ home.casesHeader.eyebrow }}</p>
          <h2>{{ home.casesHeader.title }}</h2>
          <p>{{ home.casesHeader.text }}</p>
        </div>

        <div class="mobile-carousel-bar">
          <div class="mobile-scroll-hint mobile-scroll-hint--cases" aria-hidden="true">
            <span></span>
            <strong>Σύρετε για περισσότερα έργα</strong>
          </div>
          <div class="mobile-carousel-controls" aria-label="Case studies carousel controls">
            <button type="button" aria-label="Προηγούμενο έργο" @click="scrollCaseStudies(-1)">‹</button>
            <button type="button" aria-label="Επόμενο έργο" @click="scrollCaseStudies(1)">›</button>
          </div>
        </div>

        <div ref="caseCarousel" class="case-grid horizontal-mobile horizontal-mobile--cases" aria-label="AIPath case studies">
          <article v-for="caseItem in localizedCases" :key="caseItem.title" class="case-card">
            <div class="browser-frame">
              <div class="browser-bar"><span></span><span></span><span></span></div>
              <img :src="caseItem.image" :alt="caseItem.title" loading="eager" decoding="async" :class="`case-shot case-shot--${caseItem.id}`" />
            </div>
            <div class="case-body">
              <p>{{ caseItem.category }}</p>
              <h3>{{ caseItem.title }}</h3>
              <span>{{ caseItem.text }}</span>
              <div class="case-tags">
                <small v-for="tag in caseItem.tags" :key="tag">{{ tag }}</small>
              </div>
              <a v-if="caseItem.externalUrl" :href="caseItem.externalUrl" target="_blank" rel="noopener noreferrer">{{ $t("common.viewCase") }}</a>
              <router-link v-else :to="caseItem.path">{{ $t("common.viewCase") }}</router-link>
            </div>
          </article>
        </div>
      </div>
    </section>

    <section id="research" class="alexander-section">
      <div class="container alexander-layout">
        <div class="alexander-copy">
          <p class="eyebrow">{{ home.research.eyebrow }}</p>
          <h2>{{ home.research.title }}</h2>
          <p>{{ home.research.text }}</p>
        </div>

        <div class="research-visual" aria-label="Alexander research flow">
          <div v-for="(stage, index) in localizedResearchStages" :key="stage.title" :class="['research-node', `research-node--${index + 1}`]">
            <span>{{ String(index + 1).padStart(2, '0') }}</span>
            <strong>{{ stage.title }}</strong>
          </div>
          <div class="research-axis" aria-hidden="true"></div>
          <div class="research-signal research-signal--one" aria-hidden="true"></div>
          <div class="research-signal research-signal--two" aria-hidden="true"></div>
        </div>

        <div class="research-lines">
          <article v-for="stage in localizedResearchStages" :key="stage.title">
            <strong>{{ stage.title }}</strong>
            <span>{{ stage.text }}</span>
          </article>
        </div>
      </div>
    </section>

    <section class="section section--planner">
      <div class="container planner-layout">
        <div class="planner-copy">
          <p class="eyebrow">{{ home.planner.eyebrow }}</p>
          <h2>{{ home.planner.title }}</h2>
          <p>{{ home.planner.text }}</p>
        </div>
        <article class="planner-panel">
          <div class="planner-options" role="tablist" aria-label="Project options">
            <button v-for="option in localizedPlannerOptions" :key="option.id" type="button" :class="['planner-option', { active: selectedPlanner.id === option.id }]" @click="selectedPlannerId = option.id">
              {{ option.label }}
            </button>
          </div>
          <div class="planner-result">
            <strong>{{ selectedPlanner.title }}</strong>
            <p>{{ selectedPlanner.text }}</p>
            <ul>
              <li v-for="question in selectedPlanner.questions" :key="question">{{ question }}</li>
            </ul>
            <router-link to="/project-idea" class="btn-main">{{ $t("common.startExploration") }}</router-link>
          </div>
        </article>
      </div>
    </section>

    <section class="section section--process">
      <div class="container">
        <div class="section-heading section-heading--compact">
          <p class="eyebrow">{{ home.process.eyebrow }}</p>
          <h2>{{ home.process.title }}</h2>
        </div>
        <div class="process-timeline">
          <article v-for="step in localizedProcess" :key="step.title">
            <span>{{ step.number }}</span>
            <h3>{{ step.title }}</h3>
            <p>{{ step.text }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="final-cta">
      <div class="container final-cta__inner">
        <div>
          <p class="eyebrow">{{ home.finalCta.eyebrow }}</p>
          <h2>{{ home.finalCta.title }}</h2>
          <p>{{ home.finalCta.text }}</p>
        </div>
        <router-link to="/project-idea" class="btn-main">{{ home.finalCta.button }}</router-link>
      </div>
    </section>
  </main>
</template>

<script>
export default {
  name: "AipathHomeUpgrade",
  data() {
    return {
      selectedPlannerId: "operations",
      services: [
        { index: "01", title: "Κάθετη οργάνωση επιχείρησης", text: "Ενιαίο σύστημα για ρόλους, ροές, δεδομένα και καθημερινές αποφάσεις.", features: ["Ροές εργασίας", "Ρόλοι και εγκρίσεις", "Reports"], path: "/services" },
        { index: "02", title: "Αυτοματισμοί ενεργειών", text: "Integrations και triggers που μειώνουν επαναλήψεις, καθυστερήσεις και λάθη.", features: ["API integrations", "Notifications", "Audit logs"], path: "/digital-consulting" },
        { index: "03", title: "Websites & Web Apps", text: "Σοβαρή digital παρουσία και web εργαλεία με καθαρή εμπειρία χρήσης.", features: ["SEO βάση", "Portals", "Admin panels"], path: "/website-development" },
        { index: "04", title: "AI & Alexander Research", text: "Applied AI και ελληνική γλωσσική έρευνα με πρακτική επιχειρησιακή χρήση.", features: ["Greek data", "Model layer", "AI assistants"], path: "/machine-learning-solutions" },
      ],
      cases: [
        { id: "foodeat", title: "Foodeat", category: "Vertical business platform", text: "Καθετοποιημένη εφαρμογή για εταιρεία τροφοδοσίας.", image: require("@/assets/img/case-studies/foodeat.webp"), tags: ["Orders", "Warehouse", "Delivery", "Reports"], path: "/services" },
        { id: "pandora", title: "Pandora", category: "Transport operations", text: "Εφαρμογή λειτουργίας για ανάγκες μεταφορών και πρόσβασης.", image: require("@/assets/img/case-studies/pandora.webp"), tags: ["Logistics", "Access", "Operations", "Support"], path: "/web-apps" },
        { id: "ora", title: "Ora / Workcard.gr", category: "Workforce automation", text: "Scanner και ροές για παρουσίες, χρόνους και ομάδες.", image: require("@/assets/img/case-studies/ora-workcard.webp"), tags: ["Scanner", "Time", "Teams", "Automation"], path: "/digital-consulting" },
        { id: "democrats", title: "democrats-pc.gr", category: "Website / digital presence", text: "Public site με καθαρή πληροφοριακή αρχιτεκτονική.", image: require("@/assets/img/case-studies/democrats.webp"), tags: ["Website", "Content", "SEO", "Responsive"], path: "/website-development" },
        { id: "adopto", title: "Adopto.gr", category: "Mobile / social impact app", text: "Πλατφόρμα υιοθεσιών με mobile εμπειρία για πολίτες και φορείς.", image: require("@/assets/img/case-studies/adopto.webp"), tags: ["Mobile", "Adoptions", "CMS", "UX"], path: "/mobile-apps" },
      ],
      caseUrls: {
        foodeat: "https://foodeat-app.gr/",
        pandora: "https://pandora.aipath.gr/login",
        ora: "https://workcard.gr",
        democrats: "https://democrats-pc.gr/",
        adopto: "https://adopto.gr",
      },
      researchStages: [
        { title: "Greek Data", text: "Ελληνικό corpus, domain knowledge και καθαρή προετοιμασία δεδομένων." },
        { title: "Model Layer", text: "Retrieval, prompting, inference και λογική ορίων για παραγωγική χρήση." },
        { title: "Evaluation", text: "Έλεγχος απαντήσεων, logs και μετρήσιμη βελτίωση ποιότητας." },
        { title: "Business Usage", text: "Σύνδεση με εφαρμογές, διαδικασίες και εργαλεία καθημερινής εργασίας." },
      ],
      plannerOptions: [
        { id: "operations", label: "Οργάνωση επιχείρησης", title: "Χαρτογράφηση λειτουργίας", text: "Ξεκινάμε από τμήματα, ρόλους, δεδομένα και σημεία συμφόρησης.", questions: ["Ποια διαδικασία καθυστερεί περισσότερο;", "Ποιοι ρόλοι εγκρίνουν ή ενημερώνουν;", "Πού κρατούνται σήμερα τα δεδομένα;"] },
        { id: "automation", label: "Αυτοματισμοί", title: "Ροές που γίνονται αυτόματα", text: "Εντοπίζουμε επαναλήψεις, integrations και ειδοποιήσεις που αξίζει να αυτοματοποιηθούν.", questions: ["Ποιες ενέργειες γίνονται καθημερινά με το χέρι;", "Ποια συστήματα πρέπει να μιλούν μεταξύ τους;", "Ποια λάθη κοστίζουν χρόνο;"] },
        { id: "web", label: "Web εφαρμογή", title: "Εργαλείο καθημερινής χρήσης", text: "Ορίζουμε χρήστες, δικαιώματα, dashboards και data model.", questions: ["Ποιοι χρήστες θα μπαίνουν στο σύστημα;", "Τι πρέπει να βλέπει κάθε ρόλος;", "Ποιες αναφορές χρειάζονται;"] },
        { id: "ai", label: "AI / Alexander", title: "AI με πρακτικό σκοπό", text: "Ελέγχουμε αν τα δεδομένα και οι ροές σας μπορούν να υποστηρίξουν AI χρήση.", questions: ["Ποια γνώση θέλετε να αναζητείται;", "Υπάρχουν ελληνικά έγγραφα ή συνομιλίες;", "Πού χρειάζεται αξιολόγηση απάντησης;"] },
        { id: "unknown", label: "Δεν είμαι σίγουρος", title: "Ξεκινάμε με διάγνωση", text: "Μας περιγράφετε την ανάγκη και προτείνουμε το πρώτο σωστό βήμα.", questions: ["Τι σας κουράζει περισσότερο σήμερα;", "Ποια ομάδα επηρεάζεται;", "Ποιο αποτέλεσμα θα έκανε διαφορά;"] },
      ],
      process: [
        { number: "01", title: "Discovery", text: "Καταγραφή ανάγκης, χρηστών και δεδομένων." },
        { number: "02", title: "Architecture", text: "Σύστημα, integrations και τεχνικό πλάνο." },
        { number: "03", title: "Build", text: "Υλοποίηση με πραγματικές ροές και feedback." },
        { number: "04", title: "Operate", text: "Υποστήριξη, μέτρηση και βελτίωση." },
      ],
    };
  },
  computed: {
    home() {
      const messages = this.$i18n?.getLocaleMessage(this.$i18n.locale) || {};
      const fallback = this.$i18n?.getLocaleMessage("gr") || {};
      return messages.home || fallback.home || {};
    },
    localizedServices() {
      return this.home.services || [];
    },
    localizedCases() {
      const imageMap = this.cases.reduce((items, item) => {
        items[item.id] = item.image;
        return items;
      }, {});
      return (this.home.cases || []).map((item) => ({ ...item, image: imageMap[item.id], externalUrl: this.caseUrls[item.id] }));
    },
    localizedResearchStages() {
      return this.home.research?.stages || [];
    },
    localizedPlannerOptions() {
      return this.home.planner?.options || [];
    },
    localizedProcess() {
      return this.home.process?.steps || [];
    },
    selectedPlanner() {
      return this.localizedPlannerOptions.find((option) => option.id === this.selectedPlannerId) || this.localizedPlannerOptions[0] || {};
    },
  },
  methods: {
    scrollCaseStudies(direction) {
      const carousel = this.$refs.caseCarousel;
      if (!carousel) return;

      const firstCard = carousel.querySelector(".case-card");
      const cardWidth = firstCard ? firstCard.getBoundingClientRect().width : carousel.clientWidth * 0.84;
      carousel.scrollBy({
        left: direction * (cardWidth + 14),
        behavior: "smooth",
      });
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700&display=swap");

.aipath-home-refined {
  --navy: #071B2D;
  --dark: #061524;
  --surface: #FFFFFF;
  --light: #F4F7F8;
  --green: #82F2B5;
  --text: #102437;
  --muted: #52616D;
  --border: #DDE5E9;
  font-family: "Manrope", "Inter", system-ui, sans-serif;
  color: var(--text);
  background: var(--light);
}
.aipath-home-refined :focus-visible { outline: 3px solid var(--green); outline-offset: 3px; }
.home-hero { position: relative; overflow: hidden; min-height: 650px; display: flex; align-items: center; color: #fff; background: radial-gradient(circle at 78% 52%, rgba(130,242,181,.1), transparent 34%), var(--navy); }
.hero-grid { position: absolute; inset: 0; opacity: .14; background-image: linear-gradient(rgba(255,255,255,.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.08) 1px, transparent 1px); background-size: 42px 42px; }
.hero-layout { position: relative; z-index: 1; display: grid; grid-template-columns: minmax(0, 52fr) minmax(500px, 48fr); gap: 32px; align-items: center; max-width: 1360px; padding-top: 96px; padding-bottom: 62px; }
.hero-copy { max-width: 700px; }
.eyebrow { margin: 0 0 14px; color: var(--green); font-size: 12px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; }
.hero-copy h1 { max-width: 680px; margin: 0; color: #fff; font-size: 64px; line-height: 1.04; font-weight: 650; letter-spacing: 0; }
.hero-copy p { max-width: 640px; margin: 20px 0 0; color: rgba(255,255,255,.74); font-size: 17px; line-height: 1.72; font-weight: 400; }
.hero-copy .hero-subtitle { margin-top: 22px; color: #fff; font-size: 22px; line-height: 1.45; font-weight: 500; }
.hero-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 18px; margin-top: 32px; }
.btn-main { display: inline-flex; align-items: center; justify-content: center; min-height: 50px; padding: 0 20px; border: 1px solid var(--green); border-radius: 10px; color: var(--navy); background: var(--green); font-size: 15px; font-weight: 700; transition: transform .22s ease, background .22s ease; }
.btn-main:hover { transform: translateY(-2px); background: #a7ffd0; }
.text-link { color: var(--navy); font-size: 15px; font-weight: 700; text-decoration: none; border-bottom: 1px solid currentColor; }
.text-link--light { color: rgba(255,255,255,.88); }
.hero-visual { position: relative; width: min(112%, 720px); height: 530px; min-height: 530px; margin: 0 -38px 0 0; overflow: visible; background: transparent; box-shadow: none; }
.digital-core-visual { justify-self: end; isolation: isolate; opacity: 0; animation: digitalCoreIn 760ms ease forwards; }
.digital-core-svg { display: block; width: 100%; height: 100%; overflow: visible; }
.core-fade { fill: url(#digitalCoreGlow); }
.cube-stage { transform: translate(8px, -2px) scale(1.18); transform-origin: 382px 216px; }
.digital-cube { transform-box: fill-box; transform-origin: 62% 50%; animation: digitalCubeBreath 6s ease-in-out infinite, digitalCubePerspective 10s ease-in-out infinite; }
.cube-surface { fill: rgba(11,34,51,.65); stroke: rgba(130,242,181,.48); stroke-width: 1; vector-effect: non-scaling-stroke; }
.cube-surface--top { fill: rgba(11,34,51,.58); }
.cube-surface--side { fill: rgba(8,24,39,.72); }
.cube-edge { fill: none; stroke: rgba(130,242,181,.7); stroke-width: 1.2; stroke-linejoin: round; vector-effect: non-scaling-stroke; }
.cube-edge--primary { stroke: url(#cubeEdge); stroke-width: 1.65; }
.cube-edge--soft { stroke: rgba(130,242,181,.46); }
.cube-unit--middle .cube-surface { fill: rgba(11,34,51,.46); }
.cube-unit--inner .cube-surface { fill: rgba(11,34,51,.38); }
@keyframes digitalCoreIn { from { opacity: 0; transform: translateY(10px) scale(.985); } to { opacity: 1; transform: translateY(0) scale(1); } }
@keyframes digitalCubeBreath { 0%, 100% { filter: drop-shadow(0 0 0 rgba(130,242,181,0)); } 50% { filter: drop-shadow(0 0 8px rgba(130,242,181,.18)); } }
@keyframes digitalCubePerspective { 0%, 100% { transform: scale(1.12) rotate(-1deg); } 50% { transform: scale(1.12) rotate(1.2deg); } }
.section { padding: 104px 0; }
.section--light { background: var(--light); }
.section--white { background: #fff; }
.section--planner { background: var(--light); }
.section--process { background: #fff; padding: 86px 0; }
.section-heading { max-width: 780px; margin-bottom: 40px; }
.section-heading--compact { margin-bottom: 36px; }
.section-heading h2, .planner-copy h2, .alexander-copy h2, .final-cta h2 { margin: 0; color: var(--text); font-size: clamp(34px,4vw,50px); line-height: 1.1; font-weight: 650; letter-spacing: 0; }
.section-heading p, .planner-copy p, .alexander-copy p { margin: 16px 0 0; color: var(--muted); font-size: 17px; line-height: 1.7; }
.section--process .section-heading { max-width: none; }
.section--process .section-heading h2 { white-space: nowrap; font-size: clamp(36px,3.35vw,48px); }
.service-grid { display: grid; grid-template-columns: repeat(4,minmax(0,1fr)); gap: 18px; }
#case-studies .container { width: min(100% - 48px, 1440px); max-width: 1440px; }
.case-grid { display: grid; grid-template-columns: repeat(5,minmax(0,1fr)); gap: 18px; }
.service-card, .case-card, .planner-panel { border: 1px solid var(--border); border-radius: 12px; background: var(--surface); box-shadow: 0 8px 22px rgba(16,36,55,.035); }
.service-card { min-height: 342px; display: flex; flex-direction: column; padding: 22px; }
.service-icon { display: grid; place-items: center; width: 40px; height: 40px; margin-bottom: 18px; border: 1px solid var(--border); border-radius: 10px; color: var(--navy); background: #ECF3F2; font-weight: 700; }
.service-card h3 { margin: 0 0 12px; color: var(--text); font-size: 19px; line-height: 1.28; font-weight: 700; }
.service-card p { margin: 0 0 16px; color: var(--muted); font-size: 15.5px; line-height: 1.58; }
.service-card ul { display: grid; gap: 7px; margin: 0 0 18px; padding: 0; list-style: none; }
.service-card li { position: relative; padding-left: 16px; color: #304253; font-size: 14.5px; line-height: 1.45; }
.service-card li::before { content: ""; position: absolute; left: 0; top: .62em; width: 6px; height: 6px; border-radius: 50%; background: var(--green); }
.service-card a, .case-card a { margin-top: auto; color: var(--navy); font-size: 14.5px; font-weight: 700; }
.case-card { min-height: 520px; height: auto; display: flex; flex-direction: column; overflow: hidden; }
.browser-frame { border-bottom: 1px solid var(--border); background: #fff; }
.browser-bar { height: 28px; display: flex; align-items: center; gap: 6px; padding: 0 11px; background: #F7FAFB; border-bottom: 1px solid var(--border); }
.browser-bar span { width: 7px; height: 7px; border-radius: 50%; background: #C9D5DB; }
.browser-bar span:first-child { background: #F46D62; } .browser-bar span:nth-child(2) { background: #F2BF55; } .browser-bar span:nth-child(3) { background: #42B86B; }
.browser-frame img { display: block; width: 100%; height: 156px; object-fit: cover; }
.case-shot--foodeat { object-position: 50% 14%; }
.case-shot--pandora { object-position: 67% 18%; }
.case-shot--ora { object-position: 50% 18%; }
.case-shot--democrats { object-position: 50% 10%; }
.case-shot--adopto { object-position: 50% 44%; }
.case-body { display: flex; flex-direction: column; flex: 1; min-height: 0; padding: 20px; }
.case-body > p { margin: 0 0 8px; color: var(--muted); font-size: 12px; font-weight: 700; letter-spacing: .07em; text-transform: uppercase; }
.case-body h3 { margin: 0 0 10px; color: var(--text); font-size: 23px; line-height: 1.14; font-weight: 700; }
.case-body > span { color: var(--muted); font-size: 15.5px; line-height: 1.55; }
.case-tags { display: flex; flex-wrap: wrap; gap: 6px; margin: 14px 0 16px; }
.case-tags small { padding: 4px 7px; border: 1px solid var(--border); border-radius: 8px; color: #314454; background: #F8FAFB; font-size: 11.5px; font-weight: 600; }
.alexander-section { padding: 104px 0; color: #fff; background: radial-gradient(circle at 78% 22%, rgba(130,242,181,.1), transparent 30%), var(--dark); }
.alexander-layout { display: grid; grid-template-columns: minmax(0,.9fr) minmax(390px,.66fr); gap: 54px; align-items: center; max-width: 1280px; }
.alexander-copy h2 { color: #fff; font-size: clamp(38px,4vw,56px); }
.alexander-copy p { max-width: 720px; color: rgba(255,255,255,.78); font-size: 18px; }
.research-visual { position: relative; min-height: 330px; overflow: hidden; border: 1px solid rgba(255,255,255,.12); border-radius: 12px; background: linear-gradient(145deg, rgba(12,49,69,.62), rgba(6,21,36,.88)); }
.research-axis { position: absolute; left: 14%; right: 14%; top: 50%; height: 1px; background: linear-gradient(90deg, transparent, rgba(130,242,181,.82), transparent); }
.research-node { position: absolute; width: 118px; min-height: 86px; padding: 14px; border: 1px solid rgba(130,242,181,.28); border-radius: 12px; background: rgba(6,21,36,.82); }
.research-node--1 { top: 26%; left: 10%; }
.research-node--2 { top: 54%; left: 33%; }
.research-node--3 { top: 26%; left: 56%; }
.research-node--4 { top: 54%; left: 76%; }
.research-node span { display: block; color: var(--green); font-size: 12px; font-weight: 700; }
.research-node strong { display: block; margin-top: 8px; color: #fff; font-size: 15px; line-height: 1.25; }
.research-signal { position: absolute; border: 1px solid rgba(130,242,181,.13); border-radius: 50%; }
.research-signal--one { inset: 36px 60px; } .research-signal--two { inset: 82px 110px; }
.research-lines { grid-column: 1 / -1; display: grid; gap: 0; margin-top: 8px; border-top: 1px solid rgba(255,255,255,.14); }
.research-lines article { display: grid; grid-template-columns: 180px 1fr; gap: 24px; padding: 18px 0; border-bottom: 1px solid rgba(255,255,255,.14); }
.research-lines strong { color: #fff; font-size: 18px; font-weight: 700; }
.research-lines span { color: rgba(255,255,255,.76); font-size: 16px; line-height: 1.6; }
.planner-layout { display: grid; grid-template-columns: minmax(0,.78fr) minmax(460px,.78fr); gap: 46px; align-items: center; max-width: 1280px; }
.planner-panel { padding: 24px; }
.planner-options { display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 10px; margin-bottom: 20px; }
.planner-option { min-height: 54px; padding: 0 14px; border: 1px solid var(--border); border-radius: 10px; color: var(--text); background: #fff; font-size: 15px; font-weight: 700; text-align: left; }
.planner-option.active { border-color: var(--navy); color: #fff; background: var(--navy); box-shadow: inset 0 0 0 1px rgba(130,242,181,.35); }
.planner-result { padding: 20px; border-radius: 10px; background: var(--light); }
.planner-result strong { display: block; margin-bottom: 8px; color: var(--text); font-size: 22px; }
.planner-result p { margin: 0 0 14px; color: var(--muted); line-height: 1.6; }
.planner-result ul { display: grid; gap: 8px; margin: 0 0 18px; padding-left: 18px; color: #304253; font-size: 15px; line-height: 1.45; }
.process-timeline { position: relative; display: grid; grid-template-columns: repeat(4,minmax(0,1fr)); gap: 0; border-top: 1px solid var(--border); }
.process-timeline article { position: relative; padding: 28px 24px 0 0; }
.process-timeline article::before { content: ""; position: absolute; top: -5px; left: 0; width: 10px; height: 10px; border-radius: 50%; background: var(--green); box-shadow: 0 0 0 5px #E7F8EF; }
.process-timeline span { color: var(--muted); font-size: 13px; font-weight: 700; }
.process-timeline h3 { margin: 12px 0 8px; color: var(--text); font-size: 22px; font-weight: 700; }
.process-timeline p { max-width: 240px; margin: 0; color: var(--muted); line-height: 1.5; }
.final-cta { padding: 76px 0; color: #fff; background: var(--navy); border-bottom: 1px solid rgba(255,255,255,.12); }
.final-cta__inner { display: flex; align-items: center; justify-content: space-between; gap: 30px; max-width: 1280px; }
.final-cta h2 { max-width: 780px; color: #fff; }
.final-cta p:not(.eyebrow) { max-width: 720px; margin: 14px 0 0; color: rgba(255,255,255,.72); line-height: 1.6; }
@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after { animation: none !important; transition-duration: .01ms !important; scroll-behavior: auto !important; }
  .digital-core-visual { opacity: 1; transform: none; }
}
@media (max-width: 1199px) {
  .hero-layout, .alexander-layout, .planner-layout { grid-template-columns: 1fr; }
  .hero-visual { justify-self: end; width: min(110%, 670px); max-width: 670px; height: 500px; min-height: 500px; margin-right: -20px; }
  .service-grid, .case-grid { grid-template-columns: repeat(2,minmax(0,1fr)); }
}
@media (min-width: 900px) and (max-width: 1199px) {
  .hero-layout {
    grid-template-columns: minmax(0, 52fr) minmax(380px, 44fr);
    gap: 22px;
    padding-top: 84px;
    padding-bottom: 52px;
  }
  .hero-copy h1 { font-size: 54px; max-width: 560px; }
  .hero-copy p { max-width: 540px; line-height: 1.62; }
  .hero-copy .hero-subtitle { font-size: 20px; }
  .hero-visual { justify-self: end; width: min(108%, 540px); height: 430px; min-height: 430px; overflow: visible; margin-right: -14px; }
  .digital-core-svg { transform: scale(.96); transform-origin: 58% 50%; }
}
@media (max-width: 768px) {
  .home-hero { min-height: auto; }
  .hero-layout { padding-top: 92px; padding-bottom: 50px; gap: 14px; }
  .hero-copy h1 { font-size: 42px; line-height: 1.08; }
  .hero-copy p { font-size: 16px; }
  .hero-copy .hero-subtitle { font-size: 18px; line-height: 1.45; }
  .hero-actions { gap: 14px; margin-top: 26px; }
  .hero-visual { justify-self: center; width: 100%; height: 330px; min-height: 330px; margin: -2px 0 0; overflow: hidden; }
  .digital-core-svg { transform: scale(.84) translate(-14px, -48px); transform-origin: 56% 48%; }
  .cube-stage { transform: translate(2px, 0) scale(1.1); transform-origin: 382px 216px; }
  .section { padding: 64px 0; }
  .section-heading { margin-bottom: 28px; }
  .section--process .section-heading h2 { white-space: normal; font-size: 32px; }
  .alexander-section { padding: 68px 0; }
  .research-visual { min-height: 300px; }
  .research-lines article { grid-template-columns: 1fr; gap: 6px; }
  .planner-options { grid-template-columns: 1fr; }
  .planner-panel { padding: 18px; }
  .final-cta__inner { align-items: flex-start; flex-direction: column; }
  .process-timeline { grid-template-columns: 1fr; border-top: 0; border-left: 1px solid var(--border); margin-left: 5px; }
  .process-timeline article { padding: 0 0 28px 28px; }
  .process-timeline article::before { top: 4px; left: -6px; }
}
@media (max-width: 430px) {
  .hero-copy h1 { font-size: 36px; line-height: 1.1; }
  .hero-copy p { font-size: 16px; }
  .hero-copy .hero-subtitle { font-size: 17px; }
  .hero-actions { width: 100%; align-items: flex-start; flex-direction: column; }
  .btn-main { width: 100%; min-height: 52px; }
  .text-link--light { width: auto; }
  .hero-visual { height: 310px; min-height: 310px; margin-top: 0; }
  .digital-core-svg { transform: scale(.82) translate(-20px, -54px); }
  .section-heading h2, .planner-copy h2 { font-size: 30px; }
  .section-heading p, .planner-copy p, .alexander-copy p { font-size: 16px; }
  .mobile-carousel-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin: -6px 0 18px;
  }
  .mobile-scroll-hint {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    margin: 0;
    color: var(--muted);
    font-size: 13px;
    font-weight: 800;
  }
  .mobile-scroll-hint span {
    position: relative;
    width: 42px;
    height: 1px;
    background: var(--border);
  }
  .mobile-scroll-hint span::after {
    content: "";
    position: absolute;
    top: -4px;
    right: 0;
    width: 9px;
    height: 9px;
    border-top: 1px solid var(--muted);
    border-right: 1px solid var(--muted);
    transform: rotate(45deg);
  }
  .mobile-carousel-controls {
    display: inline-flex;
    flex: 0 0 auto;
    gap: 8px;
  }
  .mobile-carousel-controls button {
    display: grid;
    place-items: center;
    width: 42px;
    height: 42px;
    border: 1px solid var(--border);
    border-radius: 10px;
    color: var(--text);
    background: #fff;
    font-size: 27px;
    font-weight: 700;
    line-height: 1;
    box-shadow: 0 6px 18px rgba(16,36,55,.045);
  }
  .mobile-carousel-controls button:active {
    transform: translateY(1px);
  }
  .horizontal-mobile { display: grid; grid-auto-flow: column; grid-auto-columns: 84vw; grid-template-columns: none; gap: 14px; overflow-x: auto; scroll-snap-type: x mandatory; padding: 0 13vw 10px 0; margin-right: -12px; -webkit-overflow-scrolling: touch; overscroll-behavior-inline: contain; }
  .horizontal-mobile--cases {
    grid-auto-columns: 82vw;
    padding-right: 18vw;
    mask-image: linear-gradient(90deg, #000 0, #000 calc(100% - 54px), transparent 100%);
  }
  .horizontal-mobile::-webkit-scrollbar { display: none; }
  .horizontal-mobile > article { scroll-snap-align: start; }
  .service-card { min-height: 322px; padding: 20px; }
  .case-card { min-height: 500px; }
  .browser-frame img { height: 148px; }
  .case-body { padding: 18px; }
  .case-body h3 { font-size: 22px; }
  .case-tags { margin-bottom: 18px; }
  .planner-layout { gap: 28px; }
  .research-visual { min-height: 340px; }
  .research-node { left: 12%; right: 12%; width: auto; }
  .research-node--1 { top: 28px; }
  .research-node--2 { top: 106px; }
  .research-node--3 { top: 184px; }
  .research-node--4 { top: 262px; }
  .research-axis { left: 50%; right: auto; top: 24px; bottom: 24px; width: 1px; height: auto; }
  .final-cta { padding: 58px 0; }
}
@media (min-width: 431px) {
  .mobile-scroll-hint,
  .mobile-carousel-bar,
  .mobile-carousel-controls {
    display: none;
  }
}
@media (max-width: 370px) {
  .hero-visual { height: 286px; min-height: 286px; }
  .digital-core-svg { transform: scale(.76) translate(-32px, -66px); }
  .horizontal-mobile--cases { grid-auto-columns: 84vw; }
}
</style>




